import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import client from 'api/client'
import { useQuery } from 'react-query'
import { useAuth } from 'hooks/UseAuth'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { MemberProfile } from 'types'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { isAlmanacCoreWorkspace } from 'utils/workspaceUtils'
import { useLocation } from 'react-router-dom'

export const useCurrentUserProfile = () => {
  const auth = useAuth()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const currentWorkspace = useCurrentWorkspace()
  const location = useLocation()
  const isDesignPage = location.pathname.startsWith('/designs/')

  // Also check if we're on login_or_signup with a design redirect
  const isLoginOrSignupWithDesignRedirect = location.pathname === '/login_or_signup' &&
    new URLSearchParams(location.search).get('redirect_to')?.startsWith('/designs/')


  const enabled = !!auth?.id &&
    !!currentWorkspaceId &&
    !!currentWorkspace &&
    !currentWorkspace?.guest &&
    !isAlmanacCoreWorkspace(currentWorkspaceId) &&
    !isDesignPage &&
    !isLoginOrSignupWithDesignRedirect && // Don't run on login_or_signup with design redirect
    // Additional safety check: ensure auth and workspace are properly synchronized
    (auth?.last_workspace_id === currentWorkspaceId || auth?.company_id === currentWorkspaceId) &&
    // Extra safety: don't run if we're in the middle of a redirect flow
    !(location.pathname === '/login_or_signup' && location.search.includes('redirect_to'))


  return useQuery<MemberProfile | undefined>(
    ['user-profile', currentWorkspaceId, auth?.id],
    async () => {
      if (!auth) return

      const res = await client.getMemberProfile(currentWorkspaceId, auth.id)

      // If the user is not a member of the workspace (404), return undefined
      // This allows cross-workspace access for shared designs
      if (res.type === 'error' && res.status === 404) {
        return undefined
      }

      if (res.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error(res.data)
      }

      return res.data
    },
    {
      enabled,
      staleTime: 60000, // 1 minute
      cacheTime: 60000, // 1 minute; must be included alongside staleTime
      refetchOnWindowFocus: false,
    },
  )
}
